/**
 * Bus sync arrière-plan / réveil app (GO_BG_SYNC P0–P2).
 * - visibilitychange / pageshow / online / Page Lifecycle resume
 * - force restart EventSource + catch-up HTTP via événements
 */

export const APP_SYNC_EVENTS = {
  RESUME: 'ucp-app-resume',
  SSE_FORCE_RESTART: 'ucp-sse-force-restart'
};

const DEBOUNCE_MS = 350;
const MIN_HIDDEN_MS = 800;

let installed = false;
let lastHiddenAt = 0;
let lastResumeAt = 0;
let debounceTimer = null;
let uninstall = null;

function dispatch(name, detail = {}) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(name, { detail }));
}

export function getLastAppResumeAt() {
  return lastResumeAt;
}

export function dispatchAppResume(reason = 'manual', extra = {}) {
  if (typeof window === 'undefined') return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    lastResumeAt = Date.now();
    const hiddenForMs = lastHiddenAt > 0 ? Math.max(0, lastResumeAt - lastHiddenAt) : 0;
    const detail = {
      reason: String(reason || 'manual'),
      resumedAt: lastResumeAt,
      hiddenForMs,
      ...extra
    };
    dispatch(APP_SYNC_EVENTS.RESUME, detail);
    dispatch(APP_SYNC_EVENTS.SSE_FORCE_RESTART, detail);
  }, DEBOUNCE_MS);
}

function shouldFireFromHidden() {
  if (!lastHiddenAt) return true;
  return Date.now() - lastHiddenAt >= MIN_HIDDEN_MS;
}

/**
 * Installe les écouteurs une seule fois (idempotent).
 * @returns {() => void} désinstalle
 */
export function installBackgroundAppSyncWatchers() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }
  if (installed) return uninstall || (() => {});

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      lastHiddenAt = Date.now();
      return;
    }
    if (!shouldFireFromHidden()) return;
    dispatchAppResume('visibilitychange');
  };

  const onPageShow = (event) => {
    dispatchAppResume(event?.persisted ? 'pageshow-bfcache' : 'pageshow');
  };

  const onOnline = () => {
    dispatchAppResume('online');
  };

  const onFreeze = () => {
    lastHiddenAt = Date.now();
  };

  const onResume = () => {
    dispatchAppResume('page-lifecycle-resume');
  };

  const onSwMessage = (event) => {
    const data = event?.data;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'ucp-push-click' || data.type === 'ucp-app-resume') {
      dispatchAppResume(data.type === 'ucp-push-click' ? 'notificationclick' : 'sw-message', {
        url: data.url || null,
        notificationId: data.notificationId || null
      });
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('online', onOnline);
  document.addEventListener('freeze', onFreeze);
  document.addEventListener('resume', onResume);
  if (navigator.serviceWorker?.addEventListener) {
    navigator.serviceWorker.addEventListener('message', onSwMessage);
  }

  installed = true;
  uninstall = () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('online', onOnline);
    document.removeEventListener('freeze', onFreeze);
    document.removeEventListener('resume', onResume);
    if (navigator.serviceWorker?.removeEventListener) {
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    }
    if (debounceTimer) clearTimeout(debounceTimer);
    installed = false;
    uninstall = null;
  };

  return uninstall;
}

/** Tests unitaires uniquement. */
export function __resetBackgroundAppSyncForTests() {
  if (typeof uninstall === 'function') uninstall();
  lastHiddenAt = 0;
  lastResumeAt = 0;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;
  installed = false;
  uninstall = null;
}
